import type { Editor } from "@tiptap/core";
import type { EditorProps } from "@tiptap/pm/view";
import type { RefObject } from "react";

// 이미지 노드는 ProseMirror에서 크기 1을 차지
const IMAGE_NODE_SIZE = 1;

/**
 * 상품 설명 에디터(TipTap) 이미지 업로드 유틸.
 *
 * 흐름: paste/drop → 이미지 파일만 추출 → placeholder(blob URL) 삽입 → NHN 업로드 
 * 성공 시 해당 노드 src를 반환 URL로 교체 & revoke,
 * 실패 시 
 *   1. 'Product not saved'면 placeholder 제거, 
 *   2. 아니면 alert 후 유지. */

/** 문서에서 src가 일치하는 이미지 노드의 위치를 반환. 없으면 null. */
function findImagePositionBySrc(editor: Editor, src: string): number | null {
  let targetPos: number | null = null;
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === "image" && node.attrs.src === src) {
      targetPos = pos;
      return false;
    }
  });
  return targetPos;
}

/**
 * 문서에서 src가 oldSrc인 이미지 노드를 찾아 src를 newSrc로 바꿈.
 */
export function replaceImageSrc(editor: Editor, oldSrc: string, newSrc: string): void {
  const pos = findImagePositionBySrc(editor, oldSrc);
  if (pos == null) return;
  editor.chain().focus().setNodeSelection(pos).updateAttributes("image", { src: newSrc }).run(); 
}

export type UploadContentImageFn = (file: File) => Promise<string>;

/**
 * placeholder 이미지 삽입 후 업로드 호출, 완료 시 에디터에서 해당 노드를 최종 URL로 교체.
 */
export async function uploadContentImageAndReplace(
  editor: Editor,
  file: File,
  placeholderSrc: string,
  uploadFn: UploadContentImageFn,
): Promise<void> {
  const url = await uploadFn(file);
  replaceImageSrc(editor, placeholderSrc, url);
  URL.revokeObjectURL(placeholderSrc);
}

/** FileList에서 이미지 파일만 추출해 배열로 반환. */
function getImageFiles(files: FileList | undefined): File[] {
  if (!files?.length) return [];
  return Array.from(files).filter((f) => f.type.startsWith("image/"));
}

/** placeholder 이미지 노드를 찾아 삭제하고 blob URL을 revoke. */
function removePlaceholderImageAndRevoke(editor: Editor, placeholderSrc: string): void {
  const pos = findImagePositionBySrc(editor, placeholderSrc);
  if (pos != null) {
    editor.chain().focus().setNodeSelection(pos).deleteSelection().run();
  }
  URL.revokeObjectURL(placeholderSrc);
}

/** 업로드 실패 시: 실패한 이미지는 항상 제거, 단 'Product not saved'가 아닐 때만 alert. */
function handleImageUploadError(editor: Editor, placeholderSrc: string, error: unknown): void {
  console.error("이미지 업로드 실패:", error);

  // 업로드 실패한 이미지는 항상 제거
  if (!(error instanceof Error && error.message === "Product not saved")) {
    alert("이미지 업로드에 실패했습니다. 다시 시도해주세요.");
  }
  removePlaceholderImageAndRevoke(editor, placeholderSrc);
}

/** 에디터에 placeholder 이미지 삽입. atPos가 있으면 해당 위치, 없으면 커서 위치. */
function insertPlaceholderImage(editor: Editor, placeholderSrc: string, atPos?: number): void {
  if (atPos != null) {
    editor.chain().focus().setTextSelection(atPos).setImage({ src: placeholderSrc }).run();
  } else {
    editor.chain().focus().setImage({ src: placeholderSrc }).run();
  }
}

/**
 * TipTap useEditor의 editorProps에 넣을 handlePaste/handleDrop 객체 생성.
 * paste/drop 시 이미지(복수 가능) → placeholder 삽입 → 업로드 → URL 교체.
 */
export function createContentImageEditorProps(
  editorRef: RefObject<Editor | null>,
  uploadFn: UploadContentImageFn,
): Pick<EditorProps, "handlePaste" | "handleDrop">
{
  return {
    handlePaste(_view, event) {
      const editor = editorRef.current;
      if (!editor) return false;

      const imageFiles = getImageFiles(event.clipboardData?.files);
      if (imageFiles.length === 0) return false;

      event.preventDefault();
      let insertPos = editor.state.selection.from;

      imageFiles.forEach((file) => {
        const placeholderSrc = URL.createObjectURL(file);
        insertPlaceholderImage(editor, placeholderSrc, insertPos);

        insertPos += IMAGE_NODE_SIZE;

        uploadContentImageAndReplace(editor, file, placeholderSrc, uploadFn).catch((err) =>
          handleImageUploadError(editor, placeholderSrc, err),
        );
      });

      return true;
    },
    handleDrop(view, event) {
      const editor = editorRef.current;
      if (!editor) return false;

      const imageFiles = getImageFiles(event.dataTransfer?.files);
      if (imageFiles.length === 0) return false;

      const pos = view.posAtCoords({ left: event.clientX, top: event.clientY });
      if (pos == null) return false;

      event.preventDefault();
      let insertPos = pos.pos;

      imageFiles.forEach((file) => {
        const placeholderSrc = URL.createObjectURL(file);
        insertPlaceholderImage(editor, placeholderSrc, insertPos);

        insertPos += IMAGE_NODE_SIZE;

        uploadContentImageAndReplace(editor, file, placeholderSrc, uploadFn).catch((err) =>
          handleImageUploadError(editor, placeholderSrc, err),
        );
      });

      return true;
    },
  };
}
